<script setup lang="ts">
   import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
   import 'mmenu-light/dist/mmenu-light.css';
   import LmsNavListItem from '~/components/LmsNavListItem.vue';
   import LmsLogoutButton from '~/components/LmsLogoutButton.vue';
   import { useUserProfileMenu } from '~/composables/useUserProfileMenu';
   import NuxtTypedLink from '~/components/NuxtTypedLink.vue';
   import { useLmsNavigation } from '~/composables/useLmsNavigation';
   import { usePortalSwitcher } from '~/composables/usePortalSwitcher';

   const route = useRoute();

   const { profileNavigationItems, showProfileFeatures, adminMenuItem } = useUserProfileMenu();
   const { settingsLoaded, visibleNavItems, getNavItemRoute, isExternalLink } = useLmsNavigation();
   const { availablePortals, currentPortal, showPortalSwitcher, switchPortal } = usePortalSwitcher();

   const menuElementRef = ref(null);
   let drawerInstance: any = null;

   type MenuItem = {
      label: string;
      to?: any;
      items?: MenuItem[];
      isExternal?: boolean;
   };

   const items = computed<MenuItem[]>(() => {
      return visibleNavItems.value.map(item => {
         const route = getNavItemRoute(item);
         const children = item.children?.length
            ? item.children.map(child => ({
               label: child.label,
               to: getNavItemRoute(child),
               isExternal: isExternalLink(child.key),
            }))
            : undefined;

         if (children?.length) {
            return { label: item.label, items: children };
         }

         return {
            label: item.label,
            to: route,
            isExternal: isExternalLink(item.key),
         };
      });
   });

   onMounted(async () => {
      if (!menuElementRef.value) {
         return;
      }

      // mmenu-light touches browser globals at import time — load it client-side only.
      const { default: MmenuLight } = await import('mmenu-light');
      const menu = new MmenuLight(menuElementRef.value, '(max-width: 1023px)');

      menu.navigation({});
      drawerInstance = menu.offcanvas({});
   });

   function openDrawer() {
      if (drawerInstance) {
         drawerInstance.open();
      }
   }

   function closeDrawer() {
      if (drawerInstance) {
         drawerInstance.close();
      }
   }

   // switchPortal refetches the profile in place and never changes the route, so the
   // fullPath watcher below will not fire — the drawer has to be closed by hand or it
   // stays open over the reloaded page.
   async function handlePortalSwitch(portalSlug: string) {
      closeDrawer();

      await switchPortal(portalSlug);
   }

   // mmenu-light builds its drawer outside the Vue tree: `offcanvas()` appends a
   // `div.mm-ocd` to <body> and, while the media query matches, *moves* this component's
   // <nav> into it, leaving a comment node behind at the original location. So when the
   // layout unmounts, Vue removes the layout root but not the relocated nav — it stays in
   // <body>, still carrying `mm-ocd--open`, as a frozen panel sitting over whatever
   // renders next.
   //
   // Logout is where that shows: the app leaves layouts/Lms.vue for the layout-less
   // welcome screen, and the drawer stayed on top of the hero video with the stale menu
   // still in it. The route watcher below cannot cover this — it is a component-scoped
   // effect, so it is disposed in the same flush that unmounts the layout and its callback
   // never runs.
   //
   // close() alone is not enough either: it only drops the open class, leaving the markup
   // behind to reappear the next time something opens the drawer.
   onBeforeUnmount(() => {
      if (!drawerInstance) {
         return;
      }

      drawerInstance.close();
      drawerInstance.wrapper?.remove();
   });

   defineExpose({
      openMenu: openDrawer,
   });

   watch(() => route.fullPath, closeDrawer);
</script>

<template>
   <nav ref="menuElementRef" class="mx-auto" id="app-main-menu">
      <ul class="flex">
         <template v-for="item in items" :key="item.label">
            <!-- Dropdown parent with children -->
            <li
               v-if="item.items && item.items.length > 0"
               class="group relative flex">
               <span
                  class="flex cursor-pointer items-center justify-between gap-1 whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                  {{ item.label }}
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     fill="currentColor"
                     class="hidden size-4 lg:block">
                     <path
                        fill-rule="evenodd"
                        d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                        clip-rule="evenodd" />
                  </svg>
               </span>
               <ul
                  class="lg:invisible lg:absolute lg:left-0 lg:top-full lg:z-50 lg:min-w-48 lg:border lg:border-gray-200 lg:bg-white lg:opacity-0 lg:shadow-lg lg:transition-all lg:duration-200 lg:group-hover:visible lg:group-hover:opacity-100">
                  <template v-for="subItem in item.items" :key="subItem.label">
                     <li v-if="subItem.to" class="flex">
                        <a
                           v-if="subItem.isExternal"
                           :href="subItem.to"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="flex w-full items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                           {{ subItem.label }}
                        </a>
                        <NuxtTypedLink
                           v-else
                           :to="subItem.to"
                           class="flex w-full items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                           {{ subItem.label }}
                        </NuxtTypedLink>
                     </li>
                  </template>
               </ul>
            </li>

            <!-- External link (e.g. members_portal) -->
            <li v-else-if="item.isExternal && item.to" class="flex">
               <a
                  :href="item.to"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                  {{ item.label }}
               </a>
            </li>

            <!-- Regular internal link -->
            <LmsNavListItem v-else-if="item.to" :to="item.to">
               {{ item.label }}
            </LmsNavListItem>
         </template>

         <!-- Mobile profile submenu -->
         <li
            v-if="settingsLoaded && showProfileFeatures"
            class="sm:hidden">
            <span
               class="flex items-center justify-between border-b-[3px] border-transparent bg-transparent p-3 text-sm font-semibold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
               Profile
            </span>
            <ul>
               <LmsNavListItem
                  :to="profileSubmenuItem.to"
                  v-for="profileSubmenuItem in profileNavigationItems"
                  :key="profileSubmenuItem.label">
                  {{ profileSubmenuItem.label }}
               </LmsNavListItem>
               <li v-if="adminMenuItem">
                  <a
                     :href="adminMenuItem.url"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="flex items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                     Admin
                  </a>
               </li>
            </ul>
         </li>

         <!-- Nothing in production. The drawer is the one surface reachable from every
              screen, so this is where the answer lives once the user is past the welcome
              screen. See components/EnvironmentBadge.vue. -->
         <li>
            <EnvironmentBadge />
         </li>

         <!-- Portal switcher and logout, phones only.
              Below sm the UserMenu that hosts both on desktop is hidden
              (LmsMainNav: `hidden sm:flex`), which left a phone user unable to change
              portal or sign out at all.

              A native <select> deliberately, matching the desktop control rather than
              rebuilding it as menu panels: it gives iOS its own wheel picker, and
              mmenu-light's delegated click handler calls stopImmediatePropagation but
              never preventDefault, so opening the picker — a default action, not a
              listener — still works.

              `!hidden` because mmenu-light's `.mm-spn li { display: block }` is more
              specific than a plain `sm:hidden` and would otherwise win. -->
         <li v-if="showPortalSwitcher" class="sm:!hidden">
            <!-- The wrapper takes the stacking context that mmenu gives its own <a>
                 elements (relative, z-index 1, inherited background), so the panel's
                 decorative `li::before` chevron stays covered. -->
            <div class="relative z-[1] bg-inherit px-3 py-2">
               <span class="mb-1 block text-xs font-bold uppercase text-gray-400">
                  Switch Portal
               </span>
               <select
                  :value="currentPortal"
                  class="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  @change="handlePortalSwitch(($event.target as HTMLSelectElement).value)">
                  <option
                     v-for="portal in availablePortals"
                     :key="portal.portal"
                     :value="portal.portal">
                     {{ portal.brand_name }}
                  </option>
               </select>
            </div>
         </li>

         <!-- Closing on click matters because this control is *inside* the drawer. Logout
              hands off to the system browser and comes back through a route change, and
              neither closes the panel on the way — so without this the drawer stays open
              over the welcome screen, by then holding nothing but this link. -->
         <li class="sm:!hidden">
            <LmsLogoutButton location="mainNav" @click="closeDrawer" />
         </li>
      </ul>
   </nav>
</template>

<style scoped></style>
